import * as React from 'react'
import {Lang} from 'yougile-lang'
import {ArrUtil, TypeUtil, Util} from 'yougile-util'
import {
  canSeeAssignedUsers,
  DataObjectId,
  Department,
  Hub,
  isAvailableForManager,
  noBoardContainerId,
  SandboxTree
} from 'yougile-model'
import {Task} from '@extensions/task'
import {DepartmentsClient} from '../../client/departments-client'
import {PermissionsChecker} from '../../client/permissions-checker'
import {Sync} from '../../client/sync'
import {withUpdaters} from '../../containers/updaters'
import {SyncUpdater} from '../../containers/updaters/sync-updater'
import {ChatSubscribers} from '../../extensions/chat-subscribers'
import {globals} from '../../extensions/globals-base'
import {UserAccess} from '../../extensions/user-access'
import {UserUtils} from '../../extensions/user-utils'
import {TaskItem, taskItemFromHub} from '../my-tasks/task-item'
import {UserAvatar} from '../user-avatar'
import {UserName} from '../user-name'

interface Props {
  taskId: DataObjectId
}

interface InnerProps {
  task: TaskItem
  items: Array<{userId: DataObjectId; reason: string}>
}

const TaskAvailabilityInfoBase = (props: Props & InnerProps) => {
  const {items} = props
  const bem = Util.bemFrom('task-availability-info')
  return (
    <div className={bem()}>
      <div className={bem('header')}>
        <div className={bem('header-name')}>{Lang.msg.permission.userName}</div>
        <div className={bem('header-reason')}>{Lang.msg.permission.reason}</div>
      </div>
      {items.map(ur => {
        return (
          <div className={bem('row')} key={ur.userId}>
            <div className={bem('name')}>
              <UserAvatar userId={ur.userId} size={24} />{' '}
              <UserName userId={ur.userId} />
            </div>
            <div className={bem('reason')}>{ur.reason}</div>
          </div>
        )
      })}
    </div>
  )
}

// eslint-disable-next-line max-lines-per-function
const getAvailableUsers = (
  hub: Hub,
  task: TaskItem,
  box: SandboxTree,
  departments: Department[]
): Array<{userId: DataObjectId; reason: string}> => {
  if (task.isPrivate) {
    return [
      {
        userId: globals.currentUserId,
        reason: Lang.msg.permission.yourPrivateTask
      }
    ]
  }

  const allUsers = UserUtils.sortByName(box.getAllUsers(false))
  const admins = allUsers
    .filter(u => (box.users.get(u.id) || {isAdmin: false}).isAdmin)
    .map(u => ({userId: u.id, reason: Lang.msg.permission.adminReason}))
  const subscribedIds = ChatSubscribers.getSubscribedUserIds(hub)
  const calculatedHierarchy = DepartmentsClient.getHierarchy(box)

  const hasTaskAccess = (
    userId: DataObjectId,
    isAssigned: boolean,
    isSubscribed: boolean,
    ignoreAdmin = false
  ): boolean => {
    return PermissionsChecker.checkShow('ignore-departments', hub, {
      considerAsMy: isAssigned,
      considerAsWithMe: isSubscribed,
      userId,
      hasNoChildrenHint: true,
      ignoreAdmin,
      calculatedHierarchy
    })
  }

  const column = box.getHub(Task.findContainingColumnId(hub.id))

  if (column?.id === noBoardContainerId) {
    const assigned = task.assigned
      .filter(userId =>
        hasTaskAccess(userId, true, subscribedIds.includes(userId))
      )
      .map(userId => ({userId, reason: Lang.msg.permission.assignedReason}))
    const subscribed = subscribedIds
      .filter(userId =>
        hasTaskAccess(userId, task.assigned.includes(userId), true)
      )
      .map(userId => ({userId, reason: Lang.msg.permission.subscribedReason}))
    const sameDep = allUsers
      .filter(u => canSeeAssignedUsers(u.id, task.assigned, departments, true))
      .map(u => ({
        userId: u.id,
        reason: Lang.msg.permission.managerReason
      }))

    const upperManager = allUsers
      .filter(u => canSeeAssignedUsers(u.id, task.assigned, departments, false))
      .map(u => ({
        userId: u.id,
        reason: canSeeAssignedUsers(u.id, task.assigned, departments, true)
          ? Lang.msg.permission.managerReason
          : Lang.msg.permission.upperManagerReason
      }))
    return ArrUtil.unique(
      [...admins, ...assigned, ...subscribed, ...sameDep, ...upperManager],
      u => u.userId
    )
  } else {
    const project = box.getProjectFor(column)
    const inProject = allUsers
      .filter(u => {
        const role = UserAccess.getRoleInProject(u.id, project)
        if (role === 'admin' || role === 'observer' || role === 'worker') {
          return true
        }
        return hasTaskAccess(
          u.id,
          task.assigned.includes(u.id),
          subscribedIds.includes(u.id),
          true
        )
      })
      .map(u => {
        if (isAvailableForManager(u.id, task, calculatedHierarchy, true)) {
          return {
            userId: u.id,
            reason: Lang.msg.permission.managerReason
          }
        }

        return {
          userId: u.id,
          reason: Lang.msg.permission.inProjectReason
        }
      })
      .filter(TypeUtil.isTruthy)
    const assigned = task.assigned
      .filter(userId =>
        hasTaskAccess(userId, true, subscribedIds.includes(userId))
      )
      .map(userId => ({userId, reason: Lang.msg.permission.assignedReason}))
    return ArrUtil.unique([...admins, ...inProject, ...assigned], u => u.userId)
  }
}

export const TaskAvailabilityInfo = withUpdaters(
  SyncUpdater({filter: '*', companyType: 'current', debounceTime: 500})
)((props: Props) => {
  const {taskId} = props
  const box = Sync.getCurrentSandbox()
  const hub = box.get(taskId) as Hub
  if (!hub) {
    return <div />
  }
  const departments = DepartmentsClient.listDepartments(box)
  const task = taskItemFromHub(box, hub)
  const items = getAvailableUsers(hub, task, box, departments)
  return <TaskAvailabilityInfoBase {...props} task={task} items={items} />
})
